// react
import { ReactNode, useContext, useState } from 'react';

// types
import { Order, Pagination, StatsEngagement } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// context
import { ContextBanner } from '../../../../App.context';

// mui
import { Button, Container, Grid, Paper, SelectChangeEvent, Typography } from '@mui/material';

// ui
import { BannerProps } from '../../../ui/Banner/Banner';

// tables
import { TableBasicProjectsEngagement } from '../../../tables/basic/TableBasicProjectsEngagement/TableBasicProjectsEngagement';

// utils
import { defaultPagination } from '../../../../utils/defaults';

export const Adoption = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [data, setData] = useState<StatsEngagement[]>([]);
	const [order, setOrder] = useState<Order>('DESC');
	const [orderBy, setOrderBy] = useState('avgPerMonth');
	const [pagination, setPagination] = useState<Pagination>(defaultPagination);

	// fetch
	const queryParams = new URLSearchParams({
		limit: String(pagination.perPage),
		order,
		orderBy,
		page: String(pagination.page),
	});

	const { isLoading } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/stats/engagement?${queryParams.toString()}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setData(res.data);
			}
			if (res.pagination) {
				setPagination(res.pagination);
			}
		},
	});

	// fetch report
	const { fetchRequest: fetchReport, isLoading: isLoadingReport } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/reports?type=PROJECT_ENGAGEMENT`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			console.log('Response', res);

			const banner: BannerProps = {
				hasClose: true,
				message: t('alert.reportSent'),
				severity: 'success',
			};

			if (res.error) {
				banner.message = t('alert.reportFailed');
				banner.severity = 'error';
			}

			setBanner(banner);
		},
	});

	// handlers
	const handleSort = ({ order: newOrder, value }: { order: Order; value: string }) => {
		setOrder(newOrder);
		setOrderBy(value);
		setPagination(defaultPagination);
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPagination((prev) => ({ ...prev, page: newPage }));
	};

	const handleChangeRowsPerPage = (e: SelectChangeEvent<unknown>, _child: ReactNode) => {
		setPagination({ ...defaultPagination, perPage: Number(e.target.value) });
	};

	const handleExport = () => {
		fetchReport();
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Grid alignItems="flex-end" container spacing={2}>
						<Grid item xs={true}>
							<Typography variant="h2">{t('engagement')}</Typography>
						</Grid>
						<Grid item xs="auto">
							<Button disabled={isLoadingReport} onClick={handleExport} variant="contained">
								{t('emailReport')}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Paper>
						<TableBasicProjectsEngagement
							BoxFooterProps={{
								sx: {
									border: 'none',
								},
							}}
							color="grey"
							data={data}
							isLoading={isLoading}
							onSort={handleSort}
							sxBox={{
								borderRadius: 2,
							}}
							TableFooterProps={{
								hasSelectRows: true,
								onChangePage: handleChangePage,
								onChangeRowsPerPage: handleChangeRowsPerPage,
								pagination,
							}}
						/>
					</Paper>
				</Grid>
			</Grid>
		</Container>
	);
};
